import React, { useRef, useState, useCallback, useEffect, ChangeEvent } from "react";
import { P2Alert, P2Checkbox, P2TextDescription, P2TextLabel, P2Modal, P2Button, P2TextInput } from "react-bootstrap-common-script";
import FileSelectionContainer from "../../../components/container/file-selection-container/file-selection-container";
import { DocumentItem, FileItem } from "../../../models/polymer/Document.model";
import { reduce } from "lodash";
import FileUploadingButton from "../../../components/button/file-uploading-button/file-uploading-button";
import { UploadFileType } from "../../../models/enum/upload-file-type.enum";
import { namePattern } from "../../../utils/shared-pattern";
import { SolicitorCorrespondenceAdditionalInfo } from "../../../models/classes/solicitor/SolicitorCorrespondenceRequest.class";
import useIsMobile from "../../../hooks/use-is-mobile";

interface FileSelectionSegmentProps {
  files: FileItem[];
  handleFileChange?: (e: React.ChangeEvent<HTMLInputElement>, code: string, label: string, file?: File) => void;
  assignFileFields?: (file_id: string, submitted_date: string, key: string, id: string) => void;
  assignError?: (isError: boolean, id: string) => void;
  clearFileChange?: (id: string) => void;
  index?: number;
  additionalContent?: JSX.Element;
  additionalCheckbox: string[];
  additionalCheckboxOnClick: (document_code: string) => void;
  document: DocumentItem;
  type: UploadFileType;
  folderName?: string;
  additionalInfo?: SolicitorCorrespondenceAdditionalInfo;
  createHandleAdditionalInfo?: (key: keyof SolicitorCorrespondenceAdditionalInfo) => (value: string) => void;
}

const DocumentClipIcon = () => {
  return (
    <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_3475_57281)">
        <path
          d="M9.70548 20.6324L20.4531 9.69196C23.1868 6.89435 27.626 6.89435 30.3597 9.69196C33.0933 12.4777 33.0933 17.0015 30.3597 19.7991L17.5559 32.8467C15.4999 34.942 12.1704 34.942 10.126 32.8467C8.06997 30.7515 8.06997 27.3586 10.126 25.2634L22.9298 12.2158C24.2966 10.8229 26.5162 10.8229 27.8831 12.2158C29.2499 13.6086 29.2499 15.8705 27.8831 17.2634L16.3176 29.0491"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_3475_57281">
          <rect width="25" height="28" fill="white" transform="translate(8 7)" />
        </clipPath>
      </defs>
    </svg>
  );
};

function FileSelectionSegment({ index = 0, additionalInfo, createHandleAdditionalInfo, ...props }: FileSelectionSegmentProps) {
  const isChecked = props.additionalCheckbox.includes(props.document.label);

  const { isMobile } = useIsMobile();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteFileName, setDeleteFileName] = useState(String);
  const [fileId, setFileId] = useState(String);
  const [selectedDeleteFileName, setSelectedDeleteFileName] = useState(String);

  useEffect(() => {
    setSelectedDeleteFileName(
      props.files.filter((item) => item.file_content.id === fileId).length > 0
        ? props.files.filter((item) => item.file_content.id === fileId)[0].file_content.file_name || deleteFileName
        : deleteFileName
    );
  }, [fileId]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const totalSize = reduce(
    props.files.map((e) => e.file_content.size),
    (sum, n: any) => {
      return sum + n;
    },
    0
  );

  const getToDeleteItemModal = useCallback((value: boolean) => {
    setIsModalOpen(value);
  }, []);

  const getFileName = useCallback((value: string) => {
    setDeleteFileName(value);
  }, []);

  const getFileId = useCallback((value: string) => {
    setFileId(value);
  }, []);

  const secondaryButtonFunction = useCallback(() => {
    if (props.clearFileChange) {
      props.clearFileChange(fileId);
    }
    closeModal();
    setFileId("");
  }, [props.clearFileChange, fileId]);

  const handleClearFileChange = useCallback(() => {
    if (inputFile.current) {
      inputFile.current.value = "";
    }
  }, [inputFile]);

  const handleUploadButtonClick = useCallback(() => {
    if (!isChecked) {
      inputFile.current?.click();
    }
  }, [inputFile, isChecked]);

  const handleAdditionalCheckboxClick = useCallback(
    (label: string) => () => {
      props?.additionalCheckboxOnClick?.(label);
    },
    [props.additionalCheckboxOnClick]
  );

  const handleFileUploadOnChange = useCallback(
    (code: string, label: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (inputFile.current?.files && inputFile.current?.files.length > 0) {
        props?.handleFileChange?.(e, code, label, inputFile.current?.files[0]);
      }
    },
    [props.handleFileChange, inputFile]
  );

  return (
    <div>
      <div className="polymer-file-selection-segment">
        <div style={{ marginBottom: isMobile ? 4 : 16 }}>
          <P2TextDescription size={isMobile ? 16 : 18} color="#000" title={`${props.document.label}`} />
        </div>
        <div className="polymer-file-selection-segment__container">
          <div className="d-flex flex-column" style={{ flex: 1 }}>
            <P2TextDescription description_size={isMobile ? 16 : 18} description_margin={"0px"} color="#575757" description={props.document.description} />
            {props.document.sample && (
              <P2TextLabel
                bold={"400"}
                marginBottom="0"
                label={
                  <span style={{ fontSize: 14, color: "#575757" }}>
                    Please reference this{" "}
                    <a className="polymer-text-links" href={`${process.env.PUBLIC_URL}${props.document.sample}`} target={"_blank"} rel={"noreferrer"}>
                      sample
                    </a>{" "}
                    when uploading the document.
                  </span>
                }
                hasTooltip={true}
                canShowTooltipContent={false}
                tooltipPosition={"start"}
              />
            )}
          </div>
          <div className="d-flex flex-column gap-2">
            <FileSelectionContainer size={totalSize}>
              {props.files.length > 0 &&
                isChecked === false &&
                props.files.map((file) => {
                  return (
                    <FileUploadingButton
                      type={props.type}
                      getToDeleteItemModal={getToDeleteItemModal}
                      getFileName={getFileName}
                      getFileId={getFileId}
                      key={file.file_content.id}
                      file={file}
                      folderName={props.folderName}
                      id={file.file_content.id}
                      assignFileFields={props.assignFileFields}
                      assignError={props.assignError}
                      clearFileChange={handleClearFileChange}
                    />
                  );
                })}
            </FileSelectionContainer>

            {props.files.some((item) => item.is_error) && (
              <div style={{ maxWidth: "304px" }}>
                <P2Alert
                  variant={"danger"}
                  text={
                    <p style={{ margin: 0, fontSize: "1rem", fontWeight: 400, lineHeight: "1.25rem" }}>
                      File did not upload successfully. Please re-upload your file.
                    </p>
                  }
                />
              </div>
            )}
            <div className="polymer-file-selection-segment__file-button">
              <P2Button onClick={handleUploadButtonClick} variant="button-icon" endIcon={<DocumentClipIcon />} width="100%" disabled={isChecked}>
                Choose files
              </P2Button>
            </div>

            <div className="polymer-file-selection-segment__requirements">
              <p>{`1. Supported file formats are:`}</p>
              <p style={{ paddingLeft: 10 }}>{`PDF, JPG and PNG (maximum 5 MB per file)`}</p>
              <p style={{ paddingLeft: 10 }}>Maximum 10 MB combined</p>
              <p>2. Multiple documents may be uploaded</p>
            </div>
          </div>
        </div>
      </div>
      {props.document.checkbox && (
        <div className="d-flex flex-column gap-4">
          <div className="polymer-file-selection-segment__checkbox">
            <P2Checkbox
              isActive={isChecked}
              onClick={handleAdditionalCheckboxClick(props.document.label)}
              description={<p className="polymer-file-selection-segment__checkbox-text">{props.document.checkbox_label}</p>}
            />
          </div>
          {isChecked && props.document?.alert_content && (
            <div>
              <P2Alert variant={"warning"} text={props.document?.alert_content}></P2Alert>
            </div>
          )}
        </div>
      )}
      <input
        ref={inputFile}
        style={{ display: "none" }}
        type="file"
        accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"
        multiple
        value={""}
        onChange={handleFileUploadOnChange(props.document.code, props.document.label)}
      />
      <div className="polymer-small-width">
        <P2Modal
          isOpen={isModalOpen}
          onCloseModal={closeModal}
          variant="default"
          title="Delete file"
          primaryButtonText="No"
          secondaryButtonText="Yes"
          primaryButtonFunction={closeModal}
          secondaryButtonFunction={secondaryButtonFunction}
          children={
            <p className="polymer-file-selection-segment__modal-content">
              Are you sure you want to delete <span style={{ fontStyle: "italic" }}>{selectedDeleteFileName}</span> ?
            </p>
          }
        />
      </div>
      {props.document.code === "SDC01" ? (
        <div style={{ marginTop: 16 }}>          
          <P2TextInput
            required
            value={additionalInfo?.mortgage_payee}
            name="name"
            label="Cashier's order for loan redemption in favour of"
            placeholder="Please input payee name"
            type="text"
            onChange={createHandleAdditionalInfo?.("mortgage_payee")}
            maxLength={100}
          />
        </div>
      ) : null}
    </div>
  );
}

export default FileSelectionSegment;
